import * as i0 from '@angular/core';
import { Injectable, Component, Input, NgModule, ChangeDetectionStrategy, ViewChild, EventEmitter, Output } from '@angular/core';
import * as i2$1 from '@helix/platform/process/api';
import { RxProcessDefinitionCacheService, RxProcessElementSearchService, RX_PROCESS_DEFINITION, RX_PROCESS_INSTANCE } from '@helix/platform/process/api';
import { size, reject, isUndefined, sortBy, forEach, cloneDeep, defaults, extend, result, assign, map, filter, startsWith, compact, includes, find, debounce, isEqual, isEmpty, chain, some } from 'lodash';
import { ReplaySubject, forkJoin, BehaviorSubject, NEVER, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter as filter$1, tap, switchMap, map as map$1, shareReplay, takeUntil, take, skip, finalize } from 'rxjs/operators';
import { util, shapes, env, dia, V, ui } from '@clientio/rappid';
import * as i2 from '@helix/platform/utils';
import { RX_GUID } from '@helix/platform/utils';
import * as i2$2 from '@helix/platform/shared/api';
import { RxRootInjector, RX_DESIGNER_ELEMENT_SHAPE, RX_THEMING, RX_DESIGNER, RX_APPLICATION } from '@helix/platform/shared/api';
import * as i9 from '@angular/common';
import { CommonModule } from '@angular/common';
import { RuntimeViewModule } from '@helix/platform/view/runtime';
import * as i7 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i1 from '@helix/platform/process/designer';
import { RxDiagramGraph, RxProcessCanvasComponent } from '@helix/platform/process/designer';
import * as i5 from '@helix/platform/ui-kit';
import { RxBusyIndicatorModule, ValidationIssueType, RxValidationIssuesModule } from '@helix/platform/ui-kit';
import * as i1$1 from '@angular/router';
import * as i8 from '@bmc-ux/adapt-angular';
import { AdaptSidebarComponent, AdaptAlertModule, AdaptCodeViewerModule, AdaptRxTextareaModule, AdaptSidebarModule, AdaptBusyModule } from '@bmc-ux/adapt-angular';
import * as i10 from '@helix/platform/shared/components';
import { TextFormControlComponent, TextareaFormControlComponent, RxDesignerHeaderModule, RxFormBuilderModule } from '@helix/platform/shared/components';

// https://github.com/clientIO/joint/issues/817
// When the size of shape is changed, JointJS resizes shape`s body via scaling.
// IE11 & MS-Edge do not support `vector-effect`, which results in changing stroke-width.
// `refHeight` & `refWidth` attributes allow resizing rectangular sub-elements relative to the referenced element without scaling.
util.deepSupplement(shapes.bpmn.Activity.prototype.defaults, {
    attrs: {
        '.body': {
            refWidth: 1,
            refHeight: 1
        }
    },
    // 1. Removed scalable group (<g class="scalable">) from the original template of Activity element
    // to resolve the issue of restoring the element size after undo/redo operations.
    // http://clientio.freshdesk.com/support/tickets/560
    // https://jira.bmc.com/browse/DRIST-8990
    // 2. Added multi instance icons
    markup: [
        '<g class="rotatable">',
        '<rect class="body outer"/>',
        '<rect class="body inner"/>',
        env.test('svgforeignobject')
            ? '<foreignObject class="fobj"><body xmlns="http://www.w3.org/1999/xhtml"><div class="content"/></body></foreignObject>'
            : '<text class="content"/>',
        '<g class="rx-icon-container">',
        '<image class="icon-multi-instance-sequential"/>',
        '<image class="icon-multi-instance-parallel"/>',
        '<path class="sub-process"/>',
        '</g>',
        '<image class="icon"/>',
        '</g>'
    ].join('')
});
var rx;
(function (rx) {
    class BaseExpandableProcessShape extends shapes.bpmn.Activity {
        initialize(options) {
            super.initialize(options);
            const embeddedCells = this.getEmbeddedCells();
            const embedsWithoutBoundaryEventLength = size(reject(embeddedCells, 'isBoundaryEvent'));
            let modelEmbeds;
            if (embedsWithoutBoundaryEventLength === embeddedCells.length) {
                modelEmbeds = this.get('embeds');
            }
            if (this.collection && this.get('expanded') && !embedsWithoutBoundaryEventLength && isUndefined(modelEmbeds)) {
                RxRootInjector.injector
                    .get(RxProcessDefinitionCacheService)
                    .getProcessDefinition(this.get('processDefinitionName'))
                    .subscribe((processDefinition) => {
                    const flowElement = RxRootInjector.injector
                        .get(RxProcessElementSearchService)
                        .findByGuid(processDefinition, `rx-${this.get('id')}`) ||
                        {};
                    this.expand(processDefinition, RxRootInjector.injector.get(RxRappidPaperService).getGraph(flowElement));
                });
            }
        }
        expand(definition, graph) {
            graph.cells = sortBy(graph.cells, (cell) => cell.type === 'rx.SequenceFlow' || cell.type === 'rx.TextAnnotationAssociation');
            const cellModels = [];
            forEach(graph.cells, (cell) => {
                // @ts-ignore
                const CellModel = util.getByPath(this.collection.cellNamespace, cell.type, '.');
                const cellModel = new CellModel(cell);
                if (cellModel.get('expanded')) {
                    const flowElement = RxRootInjector.injector
                        .get(RxProcessElementSearchService)
                        .findByGuid(definition, `rx-${cellModel.get('id')}`) || {};
                    cellModel.expand.call(this, definition, RxRootInjector.injector.get(RxRappidPaperService).getGraph(flowElement));
                }
                cellModels.push(cellModel);
            });
            this.collection.add(cellModels);
            this.attr({
                '.sub-process': {
                    display: 'none',
                    visibility: 'hidden'
                }
            });
        }
    }
    rx.BaseExpandableProcessShape = BaseExpandableProcessShape;
    class BaseCallActivity extends BaseExpandableProcessShape {
        defaults() {
            return util.deepSupplement({
                activityType: 'call-activity',
                expanded: false,
                subProcess: true,
                attrs: {
                    path: {
                        transform: 'scale(0.3, 0.3)',
                        'ref-dy': -15
                    },
                    rect: {
                        rx: 2,
                        ry: 2
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.BaseCallActivity = BaseCallActivity;
    class BaseStaticMultiInstance extends shapes.bpmn.Activity {
        static initMultiInstanceIcons(model) {
            const attrs = cloneDeep(model.get('attrs'));
            const iconAttr = {
                width: 10,
                height: 10,
                y: 1
            };
            attrs['.sub-process'] = {
                d: 'M 0 0 L 30 0 30 30 0 30 z M 15 4 L 15 26 M 4 15 L 26 15',
                stroke: '#000000',
                fill: 'transparent',
                transform: 'scale(0.3, 0.3) translate(0, 6)'
            };
            attrs['.sub-process'].display = model.prop('subProcess') ? '' : 'none';
            attrs['.icon-multi-instance-sequential'] = cloneDeep(iconAttr);
            attrs['.icon-multi-instance-parallel'] = cloneDeep(iconAttr);
            attrs['.icon-multi-instance-sequential']['xlink:href'] =
                RX_DESIGNER_ELEMENT_SHAPE.bpmnIcons.multiInstanceSequential;
            attrs['.icon-multi-instance-parallel']['xlink:href'] = RX_DESIGNER_ELEMENT_SHAPE.bpmnIcons.multiInstanceParallel;
            attrs['.rx-icon-container'] = {
                ref: '.body',
                'x-alignment': 'middle',
                'ref-x': 0.5,
                'ref-dy': -15
            };
            defaults(attrs['.icon'], attrs.image);
            delete attrs.image;
            delete attrs.path;
            model.unset('attrs', { silent: true });
            model.set('attrs', attrs);
        }
        static updateMultiInstanceIcons(model) {
            const loopType = model.prop('loopType');
            const attrs = cloneDeep(model.get('attrs'));
            attrs['.icon-multi-instance-sequential'].display = loopType === 'true' ? '' : 'none';
            attrs['.icon-multi-instance-parallel'].display = loopType === 'false' ? '' : 'none';
            if (model.prop('subProcess')) {
                attrs['.sub-process'].transform = loopType
                    ? 'scale(0.3, 0.3) translate(50, 6)'
                    : 'scale(0.3, 0.3) translate(0, 6)';
            }
            model.unset('attrs', { silent: true });
            model.set('attrs', attrs);
        }
    }
    rx.BaseStaticMultiInstance = BaseStaticMultiInstance;
    class BaseMultiInstance extends shapes.bpmn.Activity {
        initialize(options) {
            super.initialize(options);
            if (options.multiInstanceLoopDefinition) {
                this.prop('loopType', String(options.multiInstanceLoopDefinition.isSequential), { silent: true });
            }
            BaseStaticMultiInstance.initMultiInstanceIcons(this);
            BaseStaticMultiInstance.updateMultiInstanceIcons(this);
        }
    }
    rx.BaseMultiInstance = BaseMultiInstance;
    class CallActivity extends BaseCallActivity {
        initialize(options) {
            super.initialize(options);
            if (options.multiInstanceLoopDefinition) {
                this.prop('loopType', String(options.multiInstanceLoopDefinition.isSequential), { silent: true });
            }
            BaseStaticMultiInstance.initMultiInstanceIcons(this);
            BaseStaticMultiInstance.updateMultiInstanceIcons(this);
        }
        defaults() {
            return util.deepSupplement({
                type: 'rx.CallActivity'
            }, BaseCallActivity.prototype.defaults());
        }
    }
    rx.CallActivity = CallActivity;
    class Connector extends BaseMultiInstance {
        defaults() {
            return util.deepSupplement({
                type: 'rx.Connector',
                icon: 'connector',
                attrs: {
                    '.icon': {
                        width: 12,
                        height: 12,
                        'ref-x': 3,
                        'ref-y': 3
                    },
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.Connector = Connector;
    class EndEvent extends shapes.bpmn.Event {
        defaults() {
            return util.deepSupplement({
                type: 'rx.EndEvent',
                eventType: 'end',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Event.prototype.defaults);
        }
    }
    rx.EndEvent = EndEvent;
    class ErrorBoundaryEvent extends shapes.bpmn.Event {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ErrorBoundaryEvent',
                eventType: 'intermediate',
                icon: 'errorBoundary',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Event.prototype.defaults);
        }
    }
    rx.ErrorBoundaryEvent = ErrorBoundaryEvent;
    class ErrorEndEvent extends shapes.bpmn.Event {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ErrorEndEvent',
                eventType: 'end',
                icon: 'errorEnd',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Event.prototype.defaults);
        }
    }
    rx.ErrorEndEvent = ErrorEndEvent;
    class ExclusiveGateway extends shapes.bpmn.Gateway {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ExclusiveGateway',
                icon: 'cross',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Gateway.prototype.defaults);
        }
    }
    rx.ExclusiveGateway = ExclusiveGateway;
    class ParallelGateway extends shapes.bpmn.Gateway {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ParallelGateway',
                icon: 'plus',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Gateway.prototype.defaults);
        }
    }
    rx.ParallelGateway = ParallelGateway;
    class ProcessAction extends shapes.bpmn.Activity {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ProcessAction',
                icon: 'gear',
                attrs: {
                    '.icon': {
                        width: 12,
                        height: 12,
                        'ref-x': 3,
                        'ref-y': 3
                    },
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                },
                size: {
                    width: 70,
                    height: 60
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.ProcessAction = ProcessAction;
    class ReceiveTask extends BaseMultiInstance {
        defaults() {
            return util.deepSupplement({
                type: 'rx.ReceiveTask',
                icon: 'message',
                attrs: {
                    '.icon': {
                        width: 12,
                        height: 12,
                        'ref-x': 3,
                        'ref-y': 3
                    },
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.ReceiveTask = ReceiveTask;
    class SequenceFlow extends shapes.bpmn.Flow {
        defaults() {
            return util.deepSupplement({
                type: 'rx.SequenceFlow',
                router: {
                    name: 'manhattan',
                    args: {
                        step: 5
                    }
                }
            }, shapes.bpmn.Flow.prototype.defaults);
        }
    }
    rx.SequenceFlow = SequenceFlow;
    class StartEvent extends shapes.bpmn.Event {
        defaults() {
            return util.deepSupplement({
                type: 'rx.StartEvent',
                eventType: 'start',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Event.prototype.defaults);
        }
    }
    rx.StartEvent = StartEvent;
    class SubProcess extends BaseExpandableProcessShape {
        initialize(options) {
            super.initialize(options);
            if (options.multiInstanceLoopDefinition) {
                this.prop('loopType', String(options.multiInstanceLoopDefinition.isSequential), { silent: true });
            }
            BaseStaticMultiInstance.initMultiInstanceIcons(this);
            BaseStaticMultiInstance.updateMultiInstanceIcons(this);
        }
        defaults() {
            return util.deepSupplement({
                type: 'rx.SubProcess',
                activityType: 'event-sub-process',
                expanded: false,
                subProcess: true,
                attrs: {
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.SubProcess = SubProcess;
    class TextAnnotation extends shapes.bpmn.Annotation {
        defaults() {
            return util.deepSupplement({
                type: 'rx.TextAnnotation',
                attrs: {
                    rect: {
                        rx: 2,
                        ry: 2
                    }
                }
            }, shapes.bpmn.Annotation.prototype.defaults);
        }
    }
    rx.TextAnnotation = TextAnnotation;
    class TextAnnotationAssociation extends shapes.bpmn.Flow {
        defaults() {
            return util.deepSupplement({
                type: 'rx.TextAnnotationAssociation',
                flowType: 'association'
            }, shapes.bpmn.Flow.prototype.defaults);
        }
    }
    rx.TextAnnotationAssociation = TextAnnotationAssociation;
    class TimerEvent extends shapes.bpmn.Event {
        defaults() {
            return util.deepSupplement({
                type: 'rx.TimerEvent',
                eventType: 'intermediate',
                icon: 'clock',
                attrs: {
                    '.label': {
                        fill: 'gray'
                    }
                }
            }, shapes.bpmn.Event.prototype.defaults);
        }
    }
    rx.TimerEvent = TimerEvent;
    class UserTask extends BaseMultiInstance {
        defaults() {
            return util.deepSupplement({
                type: 'rx.UserTask',
                icon: 'user',
                attrs: {
                    '.icon': {
                        width: 12,
                        height: 12,
                        'ref-x': 3,
                        'ref-y': 3
                    },
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.UserTask = UserTask;
    class WebRequest extends shapes.bpmn.Activity {
        defaults() {
            return util.deepSupplement({
                type: 'rx.WebRequest',
                icon: 'webRequest',
                attrs: {
                    '.icon': {
                        width: 12,
                        height: 12,
                        'ref-x': 3,
                        'ref-y': 3
                    },
                    rect: {
                        rx: 4,
                        ry: 4
                    }
                }
            }, shapes.bpmn.Activity.prototype.defaults);
        }
    }
    rx.WebRequest = WebRequest;
})(rx || (rx = {}));

var processShapes = /*#__PURE__*/Object.freeze({
    __proto__: null,
    get rx () { return rx; }
});

class RxDesignerCellHighlighter extends dia.CellView {
    constructor(options) {
        super({
            ...options,
            model: options.cellView.model
        });
        this.selectors = {
            bpmn: {
                'bpmn.Event': '.scalable circle.outer',
                'bpmn.Annotation': '.rotatable path.stroke',
                link: 'path.connection',
                'bpmn.Flow': 'path.connection',
                'bpmn.Gateway': '.scalable polygon.body',
                'bpmn.Activity': '.scalable rect.outer',
                'basic.Rect': '.scalable rect'
            },
            rx: {
                'rx.TextAnnotation': '.rotatable path.stroke',
                'rx.TextAnnotationAssociation': 'path.connection',
                'rx.SequenceFlow': 'path.connection',
                'rx.ParallelGateway': '.scalable polygon.body',
                'rx.ExclusiveGateway': '.scalable polygon.body',
                'rx.StartEvent': '.scalable circle.outer',
                'rx.EndEvent': '.scalable circle.outer',
                'rx.SubProcess': '.rotatable rect.outer',
                'rx.ReceiveTask': '.rotatable rect.outer',
                'rx.UserTask': '.rotatable rect.outer',
                'rx.Connector': '.rotatable rect.outer',
                'rx.TimerEvent': '.scalable circle.outer',
                'rx.WebRequest': '.rotatable rect.outer'
            }
        };
        this.cellViewHighlighterOptions = extend({}, result(this, 'options'), options.cellViewHighlighterOptions || {});
        this.cellView = options.cellView;
        this.listenTo(this.cellView.paper, 'scale translate', this.update);
        this.$el = this.cellView.$el;
    }
    initHighlighter() {
        this.cellView.model.on('remove', this.eraseHighlightAndRemove);
        this.update();
    }
    update() {
        this.eraseHighlight();
        this.drawHighlight();
    }
    eraseHighlightAndRemove(evt) {
        this.eraseHighlight();
        dia.CellView.prototype.remove.apply(this, arguments);
    }
    eraseHighlight() {
        if (this.cellViewHighlighter) {
            this.cellViewHighlighter.remove();
        }
    }
    drawHighlight() {
        let selector;
        switch (true) {
            case this.cellView.model instanceof rx.ProcessAction:
            case this.cellView.model instanceof rx.BaseCallActivity:
                selector = '.rotatable rect.outer';
                break;
            default:
                selector =
                    this.selectors.rx[this.cellView.model.prop('type')] || this.selectors.bpmn[this.cellView.model.prop('type')];
        }
        const shape = this.cellView.$el.find(selector).first();
        const highlight = shape[0] ? V(shape[0]).clone() : undefined;
        if (highlight) {
            highlight.attr({
                stroke: this.cellViewHighlighterOptions.color,
                'stroke-width': this.cellViewHighlighterOptions.strokeWidth
            });
            if (highlight.node.tagName === 'circle') {
                highlight.attr({
                    r: this.cellViewHighlighterOptions.circleRadius
                });
            }
            this.cellViewHighlighter = highlight;
            shape.closest('g').prepend(highlight.node);
        }
    }
}

class RxRappidPaperService {
    constructor(rxIdService, rxJsonParserService, rxProcessElementSearchService, rxTreeService) {
        this.rxIdService = rxIdService;
        this.rxJsonParserService = rxJsonParserService;
        this.rxProcessElementSearchService = rxProcessElementSearchService;
        this.rxTreeService = rxTreeService;
        this.green = '#89c341';
        this.gray = '#999999';
        this.red = '#f83200';
        assign(shapes.bpmn.icons, RX_DESIGNER_ELEMENT_SHAPE.bpmnIcons);
    }
    init(element, graph, shouldZoomToFit = false) {
        // : PaperScroller
        // Do not instantiate paper with width or height parameters equal to 0, (e.g element is hidden in the DOM),
        // otherwise it will cause RappidJS issue in the Firefox Browser. See DRIST-18107 for more details.
        const paper = new dia.Paper({
            width: element.width || 1,
            height: element.height || 1,
            model: new dia.Graph({}, {
                cellNamespace: processShapes
            }),
            cellViewNamespace: processShapes,
            interactive: false,
            // specifying a non-existent theme to prevent rappid from overriding our styles
            // due to 'default' theme being applied
            theme: 'rx'
        });
        const paperScroller = new ui.PaperScroller({
            autoResizePaper: true,
            paper: paper,
            padding: {
                // @ts-ignore
                padding: 10
            }
        });
        element.appendChild(paperScroller.render().el);
        paperScroller.options.paper.on('blank:pointerdown', paperScroller.startPanning);
        this.resetScroll(paperScroller);
        if (graph) {
            paperScroller.options.paper.model.fromJSON(graph);
            if (shouldZoomToFit) {
                this.zoomToFit(paperScroller);
            }
        }
        return paperScroller;
    }
    getGraph(definition) {
        const graph = this.rxJsonParserService.tryParseJson(definition.layout, {
            cells: []
        });
        graph.cells.forEach((cell) => {
            const embeddedElementIds = map(filter(graph.cells, { parent: cell.id }), 'id');
            if (startsWith(cell.type, 'rx.CallActivity')) {
                cell.type = 'rx.CallActivity';
            }
            if (startsWith(cell.type, 'rx.ProcessActions')) {
                cell.type = 'rx.ProcessAction';
            }
            if (embeddedElementIds.length) {
                cell.embeds = embeddedElementIds;
            }
            cell.processDefinitionName = definition.name;
            const flowElement = this.rxProcessElementSearchService.find(definition, {
                guid: this.rxIdService.get(cell.id.toString())
            });
            if (flowElement?.multiInstanceLoopDefinition) {
                cell.multiInstanceLoopDefinition = flowElement.multiInstanceLoopDefinition;
            }
        });
        return graph;
    }
    setGraph(paperScroller, processDefinition, processInstance, shouldZoomToFit = false) {
        const graph = this.getGraph(processDefinition);
        const paper = paperScroller.options.paper;
        paper.model.fromJSON(graph);
        if (shouldZoomToFit) {
            this.zoomToFit(paperScroller);
        }
        forEach(processInstance.activities, (activity) => {
            const activityId = this.rxIdService.getBase(activity.activityId);
            const cell = paper.findViewByModel(activityId);
            if (cell) {
                if (activity.activities.length && cell.model.get('expanded')) {
                    const subProcessActivities = this.rxTreeService.flattenTree(activity, 'activities');
                    const elements = paper.model.getElements();
                    const graphIds = map(elements, 'id');
                    const originalGraphIds = compact(map(elements, (element) => element.get('originalGuid')));
                    forEach(subProcessActivities, (subProcessActivity) => {
                        // we need to do this check in order to filter inner elements that are in process
                        // instance json but are not present in graph as their parent is not expanded
                        if (includes(graphIds, this.rxIdService.getBase(subProcessActivity.activityId))) {
                            this.highlightActivity(subProcessActivity, paper, processDefinition);
                        }
                        else if (originalGraphIds && includes(originalGraphIds, subProcessActivity.activityId)) {
                            this.highlightActivity(subProcessActivity, paper, processDefinition);
                        }
                    });
                    this.highlightActivity(activity, paper, processDefinition);
                }
                else {
                    this.highlightActivity(activity, paper, processDefinition);
                }
            }
        });
        if (processInstance.exceptionMessage) {
            // exceptionMessage will be like <activityName>(<acitivityGuid>) - [<errorMessage>]
            // RegExp - find guid from exceptionMessage
            const guidPattern = new RegExp('\\((' + RX_GUID.baseIdPattern + ')\\)', 'i');
            const activityId = guidPattern.exec(processInstance.exceptionMessage);
            if (activityId[0]) {
                const activityBaseId = this.rxIdService.getBase(activityId[1]);
                this.highlightCell(activityBaseId, paper, this.red, processDefinition);
            }
        }
        this.resetScroll(paperScroller);
    }
    highlight(cellView, color) {
        if (cellView instanceof dia.CellView) {
            const cellHighlighter = new RxDesignerCellHighlighter({
                cellView,
                cellViewHighlighterOptions: {
                    strokeWidth: 10,
                    color: color
                }
            });
            cellHighlighter.initHighlighter();
        }
    }
    highlightActivity(activity, paper, plainProcessDefinition) {
        const activityId = this.rxIdService.getBase(activity.activityId);
        const color = activity.endTime ? this.gray : this.green;
        this.highlightCell(activityId, paper, color, plainProcessDefinition);
    }
    highlightCell(cellId, paper, color, plainProcessDefinition) {
        const elements = paper.model.getElements();
        let cell;
        let cellView;
        if (!includes(map(elements, 'id'), cellId)) {
            cell = find(elements, (element) => element.get('originalGuid') === this.rxIdService.get(cellId));
            cellId = cell ? cell.id : cellId;
        }
        cellView = paper.findViewByModel(cellId);
        if (cellView) {
            this.highlight(cellView, color);
        }
        else {
            this.highlightVisibleErroredCell(cellId, false, paper, plainProcessDefinition);
            // Used to highlight errored cell inside expanded sub-processes
            paper.model.on('change', debounce(() => {
                this.highlightVisibleErroredCell(cellId, true, paper, plainProcessDefinition);
            }, 100));
        }
    }
    highlightVisibleErroredCell(cellId, isFirstEntry, paper, plainProcessDefinition) {
        const owner = this.rxProcessElementSearchService.findOwner(plainProcessDefinition, this.rxIdService.get(cellId));
        let cellView;
        let erroredElement;
        if (isFirstEntry) {
            erroredElement = find(owner.flowElements, { guid: this.rxIdService.get(cellId) });
            cellView = paper.findViewByModel(this.rxIdService.getBase(erroredElement.guid));
        }
        if (!cellView) {
            cellView = paper.findViewByModel(this.rxIdService.getBase(owner.guid));
        }
        if (cellView) {
            this.highlight(cellView, this.red);
        }
        else {
            this.highlightVisibleErroredCell(owner.guid, false, paper, plainProcessDefinition);
        }
    }
    resetScroll(paperScroller) {
        paperScroller.el.scrollTop = 0;
        paperScroller.el.scrollLeft = 0;
    }
    zoomToFit(paperScroller) {
        setTimeout(() => {
            paperScroller.zoomToFit({
                padding: 10
            });
        });
    }
}
RxRappidPaperService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRappidPaperService, deps: [{ token: i2.RxIdService }, { token: i2.RxJsonParserService }, { token: i2$1.RxProcessElementSearchService }, { token: i2.RxTreeService }], target: i0.ɵɵFactoryTarget.Injectable });
RxRappidPaperService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRappidPaperService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxRappidPaperService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i2.RxIdService }, { type: i2.RxJsonParserService }, { type: i2$1.RxProcessElementSearchService }, { type: i2.RxTreeService }]; } });

class RxProcessInstancePreviewComponent {
    constructor(element, rxProcessDefinitionCacheService, rxProcessInstanceService, rxRappidPaperService) {
        this.element = element;
        this.rxProcessDefinitionCacheService = rxProcessDefinitionCacheService;
        this.rxProcessInstanceService = rxProcessInstanceService;
        this.rxRappidPaperService = rxRappidPaperService;
        this.zoomToFit = false;
        this.processInstance = null;
        this.destroyed$ = new ReplaySubject(1);
        this.rxProcessDefinitionCacheService.registerConsumer(this.destroyed$);
    }
    ngOnInit() {
        const config$ = this.config.pipe(distinctUntilChanged(isEqual), filter$1((config) => config.processDefinitionName && config.processInstanceId), tap((config) => {
            this.onClick = config.onClick;
            this.processDefinitionName = config.processDefinitionName;
            this.processInstanceId = config.processInstanceId;
            this.zoomToFit = config.zoomToFit;
        }), switchMap((config) => {
            return forkJoin([
                this.rxProcessDefinitionCacheService.getProcessDefinition(config.processDefinitionName),
                this.rxProcessInstanceService.get(config.processDefinitionName, config.processInstanceId)
            ]).pipe(map$1(([processDefinition, processInstance]) => {
                this.processDefinition = processDefinition;
                this.processInstance = processInstance;
            }));
        }), shareReplay(1));
        config$
            .pipe(tap(() => this.initialize()), takeUntil(this.destroyed$))
            .subscribe();
    }
    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
    initialize() {
        this.paperScroller = this.rxRappidPaperService.init(this.element.nativeElement);
        this.rxRappidPaperService.setGraph(this.paperScroller, this.processDefinition, this.processInstance, this.zoomToFit);
        if (this.onClick) {
            this.paperScroller.options.paper.on('blank:pointerdown', () => {
                this.onClick({
                    processDefinition: this.processDefinition,
                    processInstance: this.processInstance,
                    cellView: null
                });
            });
            this.paperScroller.options.paper.on('cell:pointerup', (cellView) => {
                this.onClick({
                    processDefinition: this.processDefinition,
                    processInstance: this.processInstance,
                    cellView: cellView
                });
            });
        }
    }
    zoomIn() {
        this.paperScroller.zoom(0.2, { max: 4 });
    }
    zoomOut() {
        this.paperScroller.zoom(-0.2, { min: 0.2 });
    }
}
RxProcessInstancePreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewComponent, deps: [{ token: i0.ElementRef }, { token: i2$1.RxProcessDefinitionCacheService }, { token: i2$1.RxProcessInstanceService }, { token: RxRappidPaperService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessInstancePreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessInstancePreviewComponent, selector: "rx-process-instance-preview", inputs: { config: "config" }, ngImport: i0, template: '', isInline: true, styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep .joint-element{cursor:default}:host::ng-deep .joint-link{color:#f9f9fa}:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .tool-remove,:host::ng-deep .joint-link:hover .marker-vertices,:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .link-tools{opacity:0;cursor:default}:host::ng-deep .joint-link .connection-wrap:hover{opacity:0}:host::ng-deep .joint-link .connection-wrap{cursor:default}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rx-process-instance-preview', template: '', styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep .joint-element{cursor:default}:host::ng-deep .joint-link{color:#f9f9fa}:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .tool-remove,:host::ng-deep .joint-link:hover .marker-vertices,:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .link-tools{opacity:0;cursor:default}:host::ng-deep .joint-link .connection-wrap:hover{opacity:0}:host::ng-deep .joint-link .connection-wrap{cursor:default}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i2$1.RxProcessDefinitionCacheService }, { type: i2$1.RxProcessInstanceService }, { type: RxRappidPaperService }]; }, propDecorators: { config: [{
                type: Input
            }] } });

class RxProcessInstancePreviewModule {
}
RxProcessInstancePreviewModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RxProcessInstancePreviewModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewModule, declarations: [RxProcessInstancePreviewComponent], imports: [CommonModule, RuntimeViewModule, TranslateModule], exports: [RxProcessInstancePreviewComponent] });
RxProcessInstancePreviewModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewModule, imports: [CommonModule, RuntimeViewModule, TranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreviewModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RuntimeViewModule, TranslateModule],
                    declarations: [RxProcessInstancePreviewComponent],
                    exports: [RxProcessInstancePreviewComponent]
                }]
        }] });

class RxProcessElementHighlighterService {
    constructor(rxDiagramElementHighlighterService, rxIdService, rxProcessElementSearchService, rxThemingService, rxTreeService) {
        this.rxDiagramElementHighlighterService = rxDiagramElementHighlighterService;
        this.rxIdService = rxIdService;
        this.rxProcessElementSearchService = rxProcessElementSearchService;
        this.rxThemingService = rxThemingService;
        this.rxTreeService = rxTreeService;
    }
    highlightElements(paper, processDefinition, processInstance) {
        processInstance.activities.forEach((activity) => {
            const activityId = this.rxIdService.getBase(activity.activityId);
            const elementView = paper.findViewByModel(activityId);
            if (elementView) {
                if (!isEmpty(activity.activities) && elementView.model.get('expanded')) {
                    const subProcessActivities = this.rxTreeService.flattenTree(activity, 'activities');
                    elementView.on('cell:expand:done', () => {
                        const elements = paper.model.getElements();
                        const ids = map(elements, 'id');
                        const originalGuid = chain(elements)
                            .map((element) => element.get('originalGuid'))
                            .compact()
                            .value();
                        if (!elementView.model.expanding) {
                            subProcessActivities.forEach((subProcessActivity) => {
                                // we need to do this check in order to filter inner elements that are in process
                                // instance json but are not present in graph as their parent is not expanded
                                if (ids.includes(this.rxIdService.getBase(subProcessActivity.activityId))) {
                                    this.highlightActivity(subProcessActivity, paper, processDefinition);
                                }
                                else if (originalGuid?.includes(subProcessActivity.activityId)) {
                                    this.highlightActivity(subProcessActivity, paper, processDefinition);
                                }
                            });
                        }
                        this.highlightActivity(activity, paper, processDefinition);
                    });
                }
                else {
                    this.highlightActivity(activity, paper);
                }
            }
        });
        if (processInstance.exceptionMessage) {
            const guidPattern = new RegExp('((' + RX_GUID.baseIdPattern + '))', 'i');
            const execResult = guidPattern.exec(processInstance.exceptionMessage);
            if (execResult) {
                const activityId = this.rxIdService.getBase(execResult[1]);
                const elementView = paper.findViewByModel(activityId);
                if (elementView) {
                    const highlightColor = this.rxThemingService.getCssVariable('--color-danger');
                    this.rxDiagramElementHighlighterService.highlight(elementView, highlightColor);
                }
            }
        }
    }
    highlight(elementView, highlightColor) {
        this.rxDiagramElementHighlighterService.highlight(elementView, highlightColor);
    }
    highlightActivity(activity, paper, processDefinition) {
        const activityId = this.rxIdService.getBase(activity.activityId);
        const highlightColor = activity.endTime
            ? this.rxThemingService.getCssVariable(RX_THEMING.cssVariables.darkBorderColor)
            : this.rxThemingService.getCssVariable('--color-success');
        this.highlightElement(activityId, paper, highlightColor, processDefinition);
    }
    highlightElement(activityId, paper, highlightColor, processDefinition) {
        let element;
        let elementView;
        const elements = paper.model.getElements();
        const elementIds = elements.map((element) => element.id);
        if (!elementIds.includes(activityId)) {
            element = elements.find((element) => element.get('originalGuid') === this.rxIdService.get(activityId));
            activityId = element ? element.get('id') : activityId;
        }
        elementView = paper.findViewByModel(activityId);
        if (elementView) {
            this.highlight(elementView, highlightColor);
        }
        else {
            this.highlightVisibleErroredElement(activityId, false, paper, processDefinition);
            // Highlight errored cell inside expanded sub-processes
            paper.model.on('change', debounce(() => {
                this.highlightVisibleErroredElement(activityId, true, paper, processDefinition);
            }, 100));
        }
    }
    highlightVisibleErroredElement(activityId, isFirstEntry, paper, processDefinition) {
        let elementView;
        const ownerDefinition = this.rxProcessElementSearchService.findOwner(processDefinition, this.rxIdService.getBase(activityId));
        if (isFirstEntry) {
            elementView = paper.findViewByModel(this.rxIdService.getBase(activityId));
        }
        if (!elementView) {
            elementView = paper.findViewByModel(this.rxIdService.getBase(ownerDefinition.guid));
        }
        if (elementView) {
            this.highlight(elementView, this.rxThemingService.getCssVariable('--color-danger'));
        }
        else {
            this.highlightVisibleErroredElement(ownerDefinition.guid, false, paper, processDefinition);
        }
    }
}
RxProcessElementHighlighterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementHighlighterService, deps: [{ token: i1.RxDiagramElementHighlighterService }, { token: i2.RxIdService }, { token: i2$1.RxProcessElementSearchService }, { token: i2$2.RxThemingService }, { token: i2.RxTreeService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessElementHighlighterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementHighlighterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessElementHighlighterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxDiagramElementHighlighterService }, { type: i2.RxIdService }, { type: i2$1.RxProcessElementSearchService }, { type: i2$2.RxThemingService }, { type: i2.RxTreeService }]; } });

class RxProcessInstancePreview2Component {
    constructor(rxProcessDefinitionAdapterService, rxProcessDefinitionCacheService, rxProcessDesignerCacheService, rxProcessDesignerDependencyResolver, rxProcessDesignerService, rxProcessElementHighlighterService, rxProcessElementRegistryService, rxProcessInstanceService) {
        this.rxProcessDefinitionAdapterService = rxProcessDefinitionAdapterService;
        this.rxProcessDefinitionCacheService = rxProcessDefinitionCacheService;
        this.rxProcessDesignerCacheService = rxProcessDesignerCacheService;
        this.rxProcessDesignerDependencyResolver = rxProcessDesignerDependencyResolver;
        this.rxProcessDesignerService = rxProcessDesignerService;
        this.rxProcessElementHighlighterService = rxProcessElementHighlighterService;
        this.rxProcessElementRegistryService = rxProcessElementRegistryService;
        this.rxProcessInstanceService = rxProcessInstanceService;
        this.customActionsSubject = new BehaviorSubject(null);
        this.dependencyResolver$ = this.rxProcessDesignerDependencyResolver.resolve().pipe(shareReplay(1));
        this.destroyed$ = new ReplaySubject(1);
        this.busySubscription = NEVER.subscribe();
        this.graph = new RxDiagramGraph(null, {
            cellNamespace: this.rxProcessElementRegistryService.cellNamespace
        });
        this.canvasConfig$ = combineLatest([this.customActionsSubject, this.dependencyResolver$]).pipe(map$1(([customActions]) => ({
            enableMultiSelection: false,
            enableSnaplines: false,
            leftCustomActions: customActions,
            canSelectElement: () => false,
            interactive: () => false
        })));
        this.rxProcessDefinitionCacheService.registerConsumer(this.destroyed$);
        this.rxProcessDesignerCacheService.registerConsumer(this.destroyed$);
    }
    ngOnInit() {
        this.processDefinition$ = this.config.pipe(distinctUntilChanged(isEqual), switchMap((config) => this.rxProcessDefinitionCacheService.getProcessDefinition(config.processDefinitionName)), map$1((processDefinition) => this.rxProcessDefinitionAdapterService.adaptProcessDefinition(processDefinition)), tap((processDefinition) => {
            this.rxProcessDesignerCacheService.setCurrentProcessDefinition(processDefinition);
        }));
        this.processInstance$ = this.config.pipe(distinctUntilChanged(isEqual), switchMap((config) => this.rxProcessInstanceService.get(config.processDefinitionName, config.processInstanceId)));
        this.config.pipe(takeUntil(this.destroyed$)).subscribe((config) => {
            this.customActionsSubject.next(config.customActions);
        });
    }
    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
    onLoad() {
        combineLatest([this.processDefinition$, this.processInstance$, this.config])
            .pipe(take(1))
            .subscribe(([processDefinition, processInstance, config]) => {
            this.graph.clear({ silent: true, ignoreCommandManager: true });
            this.graph.fromJSON(this.rxProcessDesignerService.getDiagramLayout(processDefinition, null, processDefinition.localizableStrings));
            this.canvas.zoomToFit();
            const paper = this.canvas.getPaper();
            this.rxProcessElementHighlighterService.highlightElements(paper, processDefinition, processInstance);
            if (config.onClick) {
                const paper = this.canvas.getPaper();
                paper.on('blank:pointerdown', () => {
                    config.onClick({
                        processDefinition,
                        processInstance,
                        cellView: null
                    });
                });
                paper.on('cell:pointerup', (elementView) => {
                    config.onClick({
                        processDefinition,
                        processInstance,
                        cellView: elementView
                    });
                });
            }
            this.busySubscription.unsubscribe();
        });
    }
}
RxProcessInstancePreview2Component.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreview2Component, deps: [{ token: i1.RxProcessDefinitionAdapterService }, { token: i2$1.RxProcessDefinitionCacheService }, { token: i1.RxProcessDesignerCacheService }, { token: i2$1.RxProcessDesignerDependencyResolver }, { token: i1.RxProcessDesignerService }, { token: RxProcessElementHighlighterService }, { token: i2$1.RxProcessElementRegistryService }, { token: i2$1.RxProcessInstanceService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessInstancePreview2Component.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessInstancePreview2Component, isStandalone: true, selector: "rx-process-instance-preview2", inputs: { config: "config" }, viewQueries: [{ propertyName: "canvas", first: true, predicate: RxProcessCanvasComponent, descendants: true }], ngImport: i0, template: "<rx-process-canvas\n  *ngIf=\"canvasConfig$ | async as canvasConfig\"\n  [config]=\"canvasConfig\"\n  [graph]=\"graph\"\n  (load)=\"onLoad()\"\n></rx-process-canvas>\n\n<rx-busy-indicator [options]=\"{ busy: busySubscription }\"></rx-busy-indicator>\n", styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep rx-process-canvas{position:static}:host::ng-deep .joint-element,:host::ng-deep .joint-link{cursor:default}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i9.AsyncPipe, name: "async" }, { kind: "ngmodule", type: RuntimeViewModule }, { kind: "component", type: RxProcessCanvasComponent, selector: "rx-process-canvas", inputs: ["config", "commandManager", "graph"], outputs: ["elementSelected", "load"] }, { kind: "ngmodule", type: TranslateModule }, { kind: "ngmodule", type: RxBusyIndicatorModule }, { kind: "component", type: i5.RxBusyIndicatorComponent, selector: "rx-busy-indicator", inputs: ["options"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstancePreview2Component, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'rx-process-instance-preview2', changeDetection: ChangeDetectionStrategy.OnPush, imports: [CommonModule, RuntimeViewModule, RxProcessCanvasComponent, TranslateModule, RxBusyIndicatorModule], template: "<rx-process-canvas\n  *ngIf=\"canvasConfig$ | async as canvasConfig\"\n  [config]=\"canvasConfig\"\n  [graph]=\"graph\"\n  (load)=\"onLoad()\"\n></rx-process-canvas>\n\n<rx-busy-indicator [options]=\"{ busy: busySubscription }\"></rx-busy-indicator>\n", styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep rx-process-canvas{position:static}:host::ng-deep .joint-element,:host::ng-deep .joint-link{cursor:default}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.RxProcessDefinitionAdapterService }, { type: i2$1.RxProcessDefinitionCacheService }, { type: i1.RxProcessDesignerCacheService }, { type: i2$1.RxProcessDesignerDependencyResolver }, { type: i1.RxProcessDesignerService }, { type: RxProcessElementHighlighterService }, { type: i2$1.RxProcessElementRegistryService }, { type: i2$1.RxProcessInstanceService }]; }, propDecorators: { config: [{
                type: Input
            }], canvas: [{
                type: ViewChild,
                args: [RxProcessCanvasComponent, { static: false }]
            }] } });

const RX_PROCESS_INSTANCE_VIEWER = {
    clickableElementModelTypes: [
        RX_PROCESS_DEFINITION.processElementModelTypes.connector,
        RX_PROCESS_DEFINITION.processElementModelTypes.processAction,
        RX_PROCESS_DEFINITION.processElementModelTypes.receiveTask,
        RX_PROCESS_DEFINITION.processElementModelTypes.subProcess,
        RX_PROCESS_DEFINITION.processElementModelTypes.textAnnotation,
        RX_PROCESS_DEFINITION.processElementModelTypes.userTask,
        RX_PROCESS_DEFINITION.processElementModelTypes.webRequest
    ]
};

class RxProcessInstanceViewerService {
    constructor(rxProcessDesignerService, translateService) {
        this.rxProcessDesignerService = rxProcessDesignerService;
        this.translateService = translateService;
    }
    getSelectedActivityResultsInspectorConfig(activityModel, activityResults) {
        const config = [];
        const generalConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.common.general-items.label'),
            controls: []
        };
        if (activityModel) {
            generalConfig.controls.push({
                name: 'startTimeFormatted',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.innovation-studio.process-management.start-time.label')
                }
            }, {
                name: 'endTimeFormatted',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.complete-time.label')
                }
            }, {
                name: 'executionId',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.execution-id.label')
                }
            });
        }
        if (!isEmpty(generalConfig.controls)) {
            config.push(generalConfig);
        }
        const activityResultsConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.activity-results.label'),
            controls: []
        };
        if (activityResults?.output) {
            activityResultsConfig.controls.push({
                name: 'output',
                component: TextareaFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.definition-properties.variables.output-variable.label'),
                    rows: 5
                }
            });
        }
        if (activityResults?.webApiResponse) {
            activityResultsConfig.controls.push({
                name: 'webApiResponse',
                component: TextareaFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.output-metadata.label'),
                    rows: 5
                }
            });
        }
        if (isEmpty(activityResultsConfig.controls)) {
            activityResultsConfig.controls = map(activityResults, (_, name) => ({
                name,
                component: TextFormControlComponent,
                options: {
                    label: name
                }
            }));
        }
        if (!isEmpty(activityResultsConfig.controls)) {
            config.push(activityResultsConfig);
        }
        const multiInstanceLoopDetailsConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.multi-instance-loop-details.label'),
            controls: []
        };
        if (activityModel?.activities) {
            const multiInstanceLoopControls = [];
            activityModel.activities.forEach((activityItem) => {
                if (activityModel.activityId === activityItem.activityId) {
                    multiInstanceLoopControls.push({
                        name: 'startTimeFormatted',
                        component: TextFormControlComponent,
                        options: {
                            label: this.translateService.instant('com.bmc.arsys.rx.innovation-studio.process-management.start-time.label')
                        }
                    }, {
                        name: 'endTimeFormatted',
                        component: TextFormControlComponent,
                        options: {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.complete-time.label')
                        }
                    }, {
                        name: 'executionId',
                        component: TextFormControlComponent,
                        options: {
                            label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.execution-id.label')
                        }
                    });
                }
            });
            if (!isEmpty(multiInstanceLoopControls)) {
                multiInstanceLoopDetailsConfig.controls = multiInstanceLoopControls;
                config.push(multiInstanceLoopDetailsConfig);
            }
        }
        return isEmpty(config) ? null : config;
    }
    getInstanceInspectorConfig(processInstance) {
        const controls = [
            {
                name: 'contextKey',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.definition-properties.variables.context-key.label')
                }
            },
            {
                name: 'instanceId',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.innovation-studio.process-management.process-id.label')
                }
            },
            {
                name: 'processDefinitionVersion',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.process-version.label')
                }
            },
            {
                name: 'contextId',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.context-id.label')
                }
            },
            {
                name: 'owner',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.process-owner.label')
                }
            },
            {
                name: 'status',
                component: TextFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.client.common.status.label')
                }
            }
        ];
        if (processInstance.cancelReason) {
            controls.push({
                name: 'cancelReason',
                component: TextareaFormControlComponent,
                options: {
                    label: this.translateService.instant('com.bmc.arsys.rx.innovation-studio.process-management.cancellation-reason.label'),
                    rows: 5
                }
            });
        }
        controls.push({
            name: 'startTimeFormatted',
            component: TextFormControlComponent,
            options: {
                label: this.translateService.instant('com.bmc.arsys.rx.innovation-studio.process-management.start-time.label')
            }
        }, {
            name: 'endTimeFormatted',
            component: TextFormControlComponent,
            options: {
                label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.complete-time.label')
            }
        });
        return [
            {
                label: this.translateService.instant('com.bmc.arsys.rx.client.common.general-items.label'),
                controls: controls
            }
        ];
    }
    getVariablesInspectorConfig(processInstanceVariables) {
        const config = [];
        const inputVariablesConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.definition-properties.variables.input-variables.label'),
            controls: []
        };
        processInstanceVariables.inputParams.forEach((inputParam) => {
            inputVariablesConfig.controls.push({
                name: inputParam.name,
                component: TextareaFormControlComponent,
                options: {
                    label: inputParam.name,
                    rows: 5
                }
            });
        });
        if (!isEmpty(inputVariablesConfig.controls)) {
            config.push(inputVariablesConfig);
        }
        const outputVariablesConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.definition-properties.variables.output-variables.label'),
            controls: []
        };
        processInstanceVariables.outputParams.forEach((outputParam) => {
            outputVariablesConfig.controls.push({
                name: outputParam.name,
                component: TextareaFormControlComponent,
                options: {
                    label: outputParam.name,
                    rows: 5
                }
            });
        });
        if (!isEmpty(outputVariablesConfig.controls)) {
            config.push(outputVariablesConfig);
        }
        const localVariablesConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-designer.definition-properties.variables.local-variables.label'),
            controls: []
        };
        processInstanceVariables.localVariables.forEach((variable) => {
            localVariablesConfig.controls.push({
                name: variable.name,
                component: TextareaFormControlComponent,
                options: {
                    label: variable.name,
                    rows: 5
                }
            });
        });
        if (!isEmpty(localVariablesConfig.controls)) {
            config.push(localVariablesConfig);
        }
        const otherVariableConfig = {
            label: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.other-variables.label'),
            controls: []
        };
        processInstanceVariables.otherVariables.forEach((variable) => {
            if (variable.value) {
                otherVariableConfig.controls.push({
                    name: variable.name,
                    component: TextareaFormControlComponent,
                    options: {
                        label: variable.name,
                        rows: 5
                    }
                });
            }
        });
        if (!isEmpty(otherVariableConfig.controls)) {
            config.push(otherVariableConfig);
        }
        return isEmpty(config) ? null : config;
    }
}
RxProcessInstanceViewerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerService, deps: [{ token: i1.RxProcessDesignerService }, { token: i7.TranslateService }], target: i0.ɵɵFactoryTarget.Injectable });
RxProcessInstanceViewerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.RxProcessDesignerService }, { type: i7.TranslateService }]; } });

class RxProcessInstanceViewerComponent {
    constructor(rxCommandFactoryService, rxDateUtilService, rxDefinitionNameService, rxGlobalCacheService, rxProcessDefinitionAdapterService, rxProcessDefinitionCacheService, rxProcessDesignerCacheService, rxProcessDesignerService, rxProcessElementHighlighterService, rxProcessElementRegistryService, rxProcessInstanceService, rxProcessInstanceViewerService, rxTreeService, translateService) {
        this.rxCommandFactoryService = rxCommandFactoryService;
        this.rxDateUtilService = rxDateUtilService;
        this.rxDefinitionNameService = rxDefinitionNameService;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxProcessDefinitionAdapterService = rxProcessDefinitionAdapterService;
        this.rxProcessDefinitionCacheService = rxProcessDefinitionCacheService;
        this.rxProcessDesignerCacheService = rxProcessDesignerCacheService;
        this.rxProcessDesignerService = rxProcessDesignerService;
        this.rxProcessElementHighlighterService = rxProcessElementHighlighterService;
        this.rxProcessElementRegistryService = rxProcessElementRegistryService;
        this.rxProcessInstanceService = rxProcessInstanceService;
        this.rxProcessInstanceViewerService = rxProcessInstanceViewerService;
        this.rxTreeService = rxTreeService;
        this.translateService = translateService;
        this.close = new EventEmitter();
        this.graph = new RxDiagramGraph(null, {
            cellNamespace: this.rxProcessElementRegistryService.cellNamespace
        });
        this.errorsEmptyText = this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.no-errors.label');
        this.bundleIdSubject = new BehaviorSubject(null);
        this.definitionNameSubject = new BehaviorSubject(null);
        this.instanceIdSubject = new BehaviorSubject(null);
        this.destroyed$ = new ReplaySubject(1);
        this.isDesignModeSubject = new BehaviorSubject(true);
        this.selectedElementGuidSubject = new BehaviorSubject(null);
        this.bundleFriendlyName$ = this.bundleIdSubject.pipe(switchMap((bundleId) => this.rxGlobalCacheService.getBundleFriendlyName(bundleId)));
        this.processInstance$ = combineLatest([this.definitionNameSubject, this.instanceIdSubject]).pipe(switchMap(([definitionName, instanceId]) => this.rxProcessInstanceService.get(definitionName, instanceId)), shareReplay(1));
        this.processDefinition$ = this.processInstance$.pipe(switchMap((processInstance) => this.rxProcessDefinitionCacheService.getProcessDefinition(processInstance.processDefinitionName)), map$1((processDefinition) => this.rxProcessDefinitionAdapterService.adaptProcessDefinition(processDefinition)), tap((processDefinition) => {
            this.rxProcessDesignerCacheService.setCurrentProcessDefinition(processDefinition);
        }), shareReplay(1));
        this.instanceModel$ = this.processInstance$.pipe(map$1((processInstance) => ({
            ...processInstance,
            contextKey: processInstance.contextKey || RX_PROCESS_INSTANCE.defaultContextKey,
            endTimeFormatted: this.rxDateUtilService.formatDate(processInstance.endTime, 'medium'),
            startTimeFormatted: this.rxDateUtilService.formatDate(processInstance.startTime, 'medium')
        })), shareReplay(1));
        this.instanceInspectorConfig$ = this.instanceModel$.pipe(map$1((instanceModel) => this.rxProcessInstanceViewerService.getInstanceInspectorConfig(instanceModel)));
        this.activities$ = this.instanceModel$.pipe(map$1((instanceModel) => this.rxTreeService.flattenTree(instanceModel, 'activities')));
        this.selectedActivityModel$ = combineLatest([this.selectedElementGuidSubject, this.activities$]).pipe(map$1(([selectedElementGuid, activities]) => activities.find((activity) => activity?.activityId === selectedElementGuid) ?? null), map$1((activity) => activity
            ? {
                ...activity,
                endTimeFormatted: this.rxDateUtilService.formatDate(activity?.endTime, 'medium'),
                startTimeFormatted: this.rxDateUtilService.formatDate(activity?.startTime, 'medium')
            }
            : null), shareReplay(1));
        this.activityResults$ = this.instanceModel$.pipe(map$1((instanceModel) => instanceModel.processVariables[RX_PROCESS_INSTANCE.variableNames.activityResults]
            ? JSON.parse(instanceModel.processVariables[RX_PROCESS_INSTANCE.variableNames.activityResults])
            : null));
        this.selectedActivityResults$ = combineLatest([this.selectedElementGuidSubject, this.activityResults$]).pipe(map$1(([selectedElementGuid, activityResults]) => {
            let selectedActivityResults = null;
            if (selectedElementGuid && activityResults) {
                forEach(activityResults, (activityResult) => {
                    const selectedActivityResult = activityResult[selectedElementGuid];
                    if (selectedActivityResult?.output) {
                        selectedActivityResults = {
                            ...selectedActivityResults,
                            output: JSON.stringify(selectedActivityResult.output)
                        };
                    }
                    if (selectedActivityResult?.webApiResponse) {
                        selectedActivityResults = {
                            ...selectedActivityResults,
                            webApiResponse: JSON.stringify(selectedActivityResult.webApiResponse)
                        };
                    }
                    if (!selectedActivityResults) {
                        selectedActivityResults = selectedActivityResult;
                    }
                });
            }
            return selectedActivityResults;
        }), shareReplay(1));
        this.selectedActivityResultsModel$ = combineLatest([
            this.selectedActivityModel$,
            this.selectedActivityResults$
        ]).pipe(map$1(([selectedActivityModel, selectedActivityResults]) => ({
            ...selectedActivityModel,
            ...selectedActivityResults
        })));
        this.selectedActivityResultsInspectorConfig$ = combineLatest([
            this.selectedActivityModel$,
            this.selectedActivityResults$
        ]).pipe(map$1(([selectedActivityModel, selectedActivityResults]) => this.rxProcessInstanceViewerService.getSelectedActivityResultsInspectorConfig(selectedActivityModel, selectedActivityResults)));
        this.processVariables$ = combineLatest([this.processDefinition$, this.instanceModel$]).pipe(map$1(([processDefinition, instanceModel]) => {
            const processVariables = {
                inputParams: [],
                outputParams: [],
                localVariables: [],
                otherVariables: []
            };
            forEach(instanceModel.processVariables, (processVariableValue, processVariableName) => {
                const inputParam = processDefinition.inputParams.find((inputParam) => inputParam.name === processVariableName);
                if (inputParam) {
                    processVariables.inputParams.push({
                        name: processVariableName,
                        id: inputParam.id,
                        value: inputParam.subType
                            ? JSON.parse(processVariableValue)
                            : processVariableValue
                    });
                }
                const outputParam = processDefinition.outputParams.find((outputParam) => outputParam.name === processVariableName);
                if (outputParam) {
                    processVariables.outputParams.push({
                        name: processVariableName,
                        id: outputParam.id,
                        value: outputParam.subType
                            ? JSON.parse(processVariableValue)
                            : processVariableValue
                    });
                }
                const localVariable = processDefinition.localVariables.find((localVariable) => localVariable.name === processVariableName);
                if (localVariable) {
                    processVariables.localVariables.push({
                        name: processVariableName,
                        id: localVariable.id,
                        value: localVariable.subType ? processVariableValue : processVariableValue
                    });
                }
                if (!inputParam && !outputParam && !localVariable) {
                    if (processVariableName !== RX_PROCESS_INSTANCE.variableNames.activityResults) {
                        processVariables.otherVariables.push({
                            name: processVariableName,
                            value: processVariableValue
                        });
                    }
                }
            });
            return processVariables;
        }), shareReplay(1));
        this.variablesModel$ = this.processVariables$.pipe(map$1((processVariables) => {
            return [
                ...processVariables.inputParams,
                ...processVariables.outputParams,
                ...processVariables.localVariables,
                ...processVariables.otherVariables
            ].reduce((result, processVariable) => {
                result[processVariable.name] = processVariable.value;
                return result;
            }, {});
        }));
        this.variablesInspectorConfig$ = this.processVariables$.pipe(map$1((processVariables) => this.rxProcessInstanceViewerService.getVariablesInspectorConfig(processVariables)));
        this.canvasConfig$ = this.instanceModel$.pipe(map$1((instanceModel) => {
            const isSuspendActionDisabled = instanceModel.isStartInstanceError ||
                includes([
                    RX_PROCESS_INSTANCE.status.suspended,
                    RX_PROCESS_INSTANCE.status.errored,
                    RX_PROCESS_INSTANCE.status.completed
                ], instanceModel.status);
            const isResumeActionDisabled = instanceModel.isStartInstanceError ||
                includes([RX_PROCESS_INSTANCE.status.active, RX_PROCESS_INSTANCE.status.completed], instanceModel.status);
            const isCancelActionDisabled = instanceModel.isStartInstanceError || instanceModel.status === RX_PROCESS_INSTANCE.status.completed;
            return {
                elementRegistry: this.rxProcessElementRegistryService,
                enableMultiSelection: false,
                enableSnaplines: false,
                leftCustomActions: [
                    {
                        name: 'refresh',
                        icon: 'd-icon-refresh',
                        tooltip: this.translateService.instant('com.bmc.arsys.rx.client.common.refresh.label'),
                        execute: () => this.onRefresh()
                    },
                    ...(isSuspendActionDisabled && isResumeActionDisabled && isCancelActionDisabled
                        ? []
                        : [
                            {
                                name: 'suspend',
                                icon: 'd-icon-pause_adapt',
                                tooltip: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.suspend-process.label'),
                                isDisabled: isSuspendActionDisabled,
                                execute: () => this.executeAction('com.bmc.arsys.rx.application.process.command.SuspendProcessInstanceCommand')
                            },
                            {
                                name: 'resume',
                                icon: 'd-icon-resume_media_control_o',
                                tooltip: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.resume-process.label'),
                                isDisabled: isResumeActionDisabled,
                                execute: () => this.executeAction('com.bmc.arsys.rx.application.process.command.ActivateProcessInstanceCommand')
                            },
                            {
                                name: 'cancel',
                                icon: 'd-icon-canceled',
                                tooltip: this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.cancel-process.label'),
                                isDisabled: isCancelActionDisabled,
                                execute: () => this.executeAction('com.bmc.arsys.rx.application.process.command.CancelProcessInstanceCommand')
                            }
                        ])
                ],
                canSelectElement: (element) => {
                    const modelType = element.prop('elementModel/type');
                    return (includes(RX_PROCESS_INSTANCE_VIEWER.clickableElementModelTypes, modelType) ||
                        startsWith(modelType, RX_PROCESS_DEFINITION.processElementModelTypes.processActions) ||
                        startsWith(modelType, RX_PROCESS_DEFINITION.processElementModelTypes.callActivity));
                },
                defaultLink: (elementView) => elementView.model.get('type') === RX_PROCESS_DEFINITION.processElementModelTypes.textAnnotation
                    ? this.rxProcessElementRegistryService
                        .getStandardElement(RX_PROCESS_DEFINITION.processElementModelTypes.textAnnotationAssociation)
                        .service.getDiagramElement()
                    : this.rxProcessElementRegistryService
                        .getStandardElement(RX_PROCESS_DEFINITION.processElementModelTypes.sequenceFlow)
                        .service.getDiagramElement(),
                interactive: () => false
            };
        }));
        this.breadcrumbItems$ = combineLatest([this.processDefinition$, this.instanceModel$]).pipe(map$1(([processDefinition, instanceModel]) => [
            {
                label: this.rxDefinitionNameService.getDisplayName(processDefinition.name),
                data: {}
            },
            {
                label: instanceModel.contextKey,
                data: {}
            }
        ]));
        this.processExecutionError$ = this.instanceModel$.pipe(map$1((instanceModel) => {
            let instanceError = null;
            if (instanceModel.exceptionMessage) {
                instanceError = {
                    instanceId: instanceModel.instanceId,
                    message: instanceModel.exceptionMessage
                };
            }
            return instanceError;
        }));
        this.processExecutionErrorSections$ = this.processExecutionError$.pipe(map$1((processExecutionError) => {
            const validationIssueSections = [];
            if (processExecutionError) {
                validationIssueSections.push({
                    title: processExecutionError.instanceId,
                    issues: [
                        {
                            type: ValidationIssueType.Error,
                            description: processExecutionError.message,
                            disableCorrection: true,
                            data: {
                                propertyName: processExecutionError.instanceId,
                                inspectorTabIndex: RX_DESIGNER.sidebarPanels.definitionProperties
                            }
                        }
                    ]
                });
            }
            return validationIssueSections;
        }), shareReplay(1));
        this.hasProcessExecutionErrors$ = this.processExecutionErrorSections$.pipe(map$1((processExecutionErrorSections) => some(processExecutionErrorSections, (section) => find(section.issues, { type: ValidationIssueType.Error }))), distinctUntilChanged());
        this.processExecutionErrorSectionIcon$ = this.hasProcessExecutionErrors$.pipe(map$1((hasProcessExecutionErrors) => hasProcessExecutionErrors ? 'd-icon-exclamation_triangle text-danger' : 'd-icon-exclamation_triangle'));
        this.vm$ = combineLatest([
            this.breadcrumbItems$,
            this.bundleFriendlyName$,
            this.canvasConfig$,
            this.instanceInspectorConfig$,
            this.instanceModel$,
            this.isDesignModeSubject,
            this.processDefinition$,
            this.processExecutionErrorSectionIcon$,
            this.processExecutionErrorSections$,
            this.processInstance$,
            this.selectedActivityResultsInspectorConfig$,
            this.selectedActivityResultsModel$,
            this.variablesModel$,
            this.variablesInspectorConfig$
        ]).pipe(map$1(([breadcrumbItems, bundleFriendlyName, canvasConfig, instanceInspectorConfig, instanceModel, isDesignMode, processDefinition, processExecutionErrorSectionIcon, processExecutionErrorSections, processInstance, selectedActivityResultsInspectorConfig, selectedActivityResultsModel, variablesModel, variablesInspectorConfig]) => ({
            breadcrumbItems,
            bundleFriendlyName,
            canvasConfig,
            instanceInspectorConfig,
            instanceModel,
            isDesignMode,
            processDefinition,
            processExecutionErrorSectionIcon,
            processExecutionErrorSections,
            processInstance,
            selectedActivityResultsInspectorConfig,
            selectedActivityResultsModel,
            variablesModel,
            variablesInspectorConfig
        })));
        this.rxProcessDefinitionCacheService.registerConsumer(this.destroyed$);
        this.rxProcessDesignerCacheService.registerConsumer(this.destroyed$);
    }
    ngOnInit() {
        this.selectedElementGuidSubject.pipe(skip(1)).subscribe((guid) => {
            if (this.sidebar.isPanelOpened) {
                if (guid) {
                    this.sidebar.openPanel(2);
                }
                else {
                    this.sidebar.openPanel(0);
                }
            }
        });
    }
    ngOnChanges(changes) {
        if (changes.configuration.currentValue.definitionName && changes.configuration.currentValue.instanceId) {
            this.isDesignModeSubject.next(true);
            this.definitionNameSubject.next(changes.configuration.currentValue.definitionName);
            this.instanceIdSubject.next(changes.configuration.currentValue.instanceId);
        }
        if (changes.configuration.currentValue.bundleId !== changes.configuration.previousValue?.bundleId) {
            this.bundleIdSubject.next(changes.configuration.currentValue.bundleId);
        }
    }
    ngOnDestroy() {
        this.bundleIdSubject.complete();
        this.definitionNameSubject.complete();
        this.instanceIdSubject.complete();
        this.isDesignModeSubject.complete();
        this.selectedElementGuidSubject.complete();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
    onCloseDesigner() {
        this.close.emit();
    }
    onElementSelected(guid) {
        this.selectedElementGuidSubject.next(guid);
    }
    onLoad() {
        combineLatest([this.processDefinition$, this.processInstance$])
            .pipe(take(1))
            .subscribe(([processDefinition, processInstance]) => {
            this.graph.clear({ silent: true, ignoreCommandManager: true });
            this.graph.fromJSON(this.rxProcessDesignerService.getDiagramLayout(processDefinition, null, processDefinition.localizableStrings));
            setTimeout(() => {
                this.canvas.zoomToFit();
                const paper = this.canvas.getPaper();
                this.rxProcessElementHighlighterService.highlightElements(paper, processDefinition, processInstance);
            });
        });
    }
    onToggleDesignMode() {
        this.isDesignModeSubject.next(!this.isDesignModeSubject.value);
    }
    executeAction(command) {
        this.instanceModel$
            .pipe(take(1), switchMap((instanceModel) => this.rxCommandFactoryService
            .forResourceType(command)
            .execute({
            processDefinitionName: instanceModel.processDefinitionName,
            processInstanceId: instanceModel.instanceId
        })
            .pipe(finalize(() => {
            this.onRefresh();
        }))))
            .subscribe();
    }
    onRefresh() {
        combineLatest([this.processDefinition$, this.processInstance$])
            .pipe(take(1), switchMap(([processDefinition, processInstance]) => this.rxProcessInstanceService.get(processDefinition.name, processInstance.instanceId)))
            .subscribe((processInstance) => {
            this.definitionNameSubject.next(processInstance.processDefinitionName);
            this.instanceIdSubject.next(processInstance.instanceId);
            this.selectedElementGuidSubject.next(null);
        });
    }
}
RxProcessInstanceViewerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerComponent, deps: [{ token: i2$2.RxCommandFactoryService }, { token: i2.RxDateUtilsService }, { token: i2$2.RxDefinitionNameService }, { token: i2$2.RxGlobalCacheService }, { token: i1.RxProcessDefinitionAdapterService }, { token: i2$1.RxProcessDefinitionCacheService }, { token: i1.RxProcessDesignerCacheService }, { token: i1.RxProcessDesignerService }, { token: RxProcessElementHighlighterService }, { token: i2$1.RxProcessElementRegistryService }, { token: i2$1.RxProcessInstanceService }, { token: RxProcessInstanceViewerService }, { token: i2.RxTreeService }, { token: i7.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessInstanceViewerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessInstanceViewerComponent, selector: "rx-process-instance-viewer", inputs: { configuration: "configuration" }, outputs: { close: "close" }, viewQueries: [{ propertyName: "canvas", first: true, predicate: RxProcessCanvasComponent, descendants: true }, { propertyName: "sidebar", first: true, predicate: AdaptSidebarComponent, descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ng-container *ngIf=\"vm$ | async as vm\">\n  <rx-designer-header\n    [breadcrumbItems]=\"vm.breadcrumbItems\"\n    [bundleName]=\"vm.bundleFriendlyName\"\n    [isDesignMode]=\"vm.isDesignMode\"\n    [showSaveButton]=\"false\"\n    (closeDesigner)=\"onCloseDesigner()\"\n    (toggleDesignMode)=\"onToggleDesignMode()\"\n  ></rx-designer-header>\n\n  <div class=\"rx-designer-component\" [hidden]=\"!vm.isDesignMode\">\n    <adapt-sidebar [openedId]=\"0\" panelWidth=\"280px\" position=\"right\" #sidebar>\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.process-details.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.process-details.label' | translate }}\"\n        iconClass=\"d-icon-gear\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"process-details\"\n      >\n        <rx-form-builder\n          [config]=\"vm.instanceInspectorConfig\"\n          [model]=\"vm.instanceModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.common.variables.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.common.variables.label' | translate }}\"\n        iconClass=\"d-icon-arrows_right\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"variables\"\n      >\n        <rx-form-builder\n          *ngIf=\"vm.variablesInspectorConfig; else noVariables\"\n          [config]=\"vm.variablesInspectorConfig\"\n          [model]=\"vm.variablesModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n\n        <ng-template #noVariables>\n          <adapt-alert\n            class=\"p-3\"\n            [config]=\"{\n              content: 'com.bmc.arsys.rx.client.process-instance-viewer.no-variables.label' | translate,\n              variant: 'info',\n              type: 'inline'\n            }\"\n          ></adapt-alert>\n        </ng-template>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.activity-details.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.activity-details.label' | translate }}\"\n        iconClass=\"d-icon-arrow_chart\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"activity-details\"\n      >\n        <rx-form-builder\n          *ngIf=\"vm.selectedActivityResultsInspectorConfig; else noSelectedActivity\"\n          [config]=\"vm.selectedActivityResultsInspectorConfig\"\n          [model]=\"vm.selectedActivityResultsModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n\n        <ng-template #noSelectedActivity>\n          <adapt-alert\n            class=\"p-3\"\n            [config]=\"{\n              content: 'com.bmc.arsys.rx.client.process-instance-viewer.no-selected-activity.label' | translate,\n              variant: 'info',\n              type: 'inline'\n            }\"\n          ></adapt-alert>\n        </ng-template>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.common.errors.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.common.errors.label' | translate }}\"\n        [iconClass]=\"vm.processExecutionErrorSectionIcon\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"errors\"\n      >\n        <rx-validation-issues\n          [issueSections]=\"vm.processExecutionErrorSections\"\n          [emptyText]=\"errorsEmptyText\"\n        ></rx-validation-issues>\n      </adapt-sidebar-item>\n\n      <div class=\"main\">\n        <rx-process-canvas\n          [ngClass]=\"{\n            'inspector-expanded': sidebar.isPanelOpened\n          }\"\n          [config]=\"vm.canvasConfig\"\n          [graph]=\"graph\"\n          (elementSelected)=\"onElementSelected($event)\"\n          (load)=\"onLoad()\"\n        ></rx-process-canvas>\n      </div>\n    </adapt-sidebar>\n  </div>\n\n  <adapt-code-viewer\n    *ngIf=\"!vm.isDesignMode\"\n    class=\"full-size\"\n    theme=\"light\"\n    lang=\"javascript\"\n    [hasToolbar]=\"false\"\n    [code]=\"vm.processInstance | json\"\n  ></adapt-code-viewer>\n</ng-container>\n", styles: [":host{display:flex;flex-direction:column;height:100%;width:100%}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper{border-top:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper .adapt-sidebar-main{overflow:auto}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper .adapt-sidebar-panel-content{padding:1px 0 0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper rx-form-builder rx-form-section:first-child adapt-accordion-tab>.card,:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper rx-validation-issues adapt-accordion adapt-accordion-tab:first-of-type>.card{border-top:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper adapt-accordion-tab>.card{border-radius:0;border-left:0;border-right:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper adapt-accordion-tab>.card .card-header{border-radius:0}.rx-designer-component{height:calc(100% - 50px)}.rx-designer-container{display:flex;flex-direction:column;flex-grow:1}:root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host::ng-deep rx-process-canvas{left:0}.rx-designer-component .inspector-expanded{right:335px}\n"], dependencies: [{ kind: "component", type: i8.AdaptAlertComponent, selector: "adapt-alert", inputs: ["config"], outputs: ["onClose"] }, { kind: "component", type: i8.AdaptCodeViewerComponent, selector: "adapt-code-viewer", inputs: ["code", "theme", "lang", "texts", "hasToolbar"] }, { kind: "component", type: i8.AdaptSidebarComponent, selector: "adapt-sidebar", inputs: ["className", "navClassName", "panelWidth", "panel2Width", "position", "theme", "widthLimit", "openedId", "openedSecondaryId", "adjustMainContainerWidth"], outputs: ["openedIdChange", "isPanelOpenedCurrently"], exportAs: ["adaptSidebar"] }, { kind: "component", type: i8.AdaptSidebarItemComponent, selector: "adapt-sidebar-item", inputs: ["iconClass", "headerTitle", "tooltipText", "aria-label", "destroyOnDeactivate"] }, { kind: "directive", type: i9.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: i10.RxDesignerHeaderComponent, selector: "rx-designer-header", inputs: ["bundleName", "breadcrumbItems", "isDesignMode", "isSaveButtonDisabled", "showModeButton", "showPreviewButton", "showSaveButton"], outputs: ["breadcrumbSelected", "toggleDesignMode", "showPreview", "save", "closeDesigner"] }, { kind: "component", type: i10.FormBuilderComponent, selector: "rx-form-builder", inputs: ["config", "model", "guid", "isReadOnly", "focusEditor$"], outputs: ["modelChange", "editorEvent", "formInitialized"] }, { kind: "component", type: i1.RxProcessCanvasComponent, selector: "rx-process-canvas", inputs: ["config", "commandManager", "graph"], outputs: ["elementSelected", "load"] }, { kind: "component", type: i5.RxValidationIssuesComponent, selector: "rx-validation-issues", inputs: ["issueSections", "emptyText"], outputs: ["correctIssue"] }, { kind: "pipe", type: i9.AsyncPipe, name: "async" }, { kind: "pipe", type: i9.JsonPipe, name: "json" }, { kind: "pipe", type: i7.TranslatePipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rx-process-instance-viewer', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngIf=\"vm$ | async as vm\">\n  <rx-designer-header\n    [breadcrumbItems]=\"vm.breadcrumbItems\"\n    [bundleName]=\"vm.bundleFriendlyName\"\n    [isDesignMode]=\"vm.isDesignMode\"\n    [showSaveButton]=\"false\"\n    (closeDesigner)=\"onCloseDesigner()\"\n    (toggleDesignMode)=\"onToggleDesignMode()\"\n  ></rx-designer-header>\n\n  <div class=\"rx-designer-component\" [hidden]=\"!vm.isDesignMode\">\n    <adapt-sidebar [openedId]=\"0\" panelWidth=\"280px\" position=\"right\" #sidebar>\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.process-details.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.process-details.label' | translate }}\"\n        iconClass=\"d-icon-gear\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"process-details\"\n      >\n        <rx-form-builder\n          [config]=\"vm.instanceInspectorConfig\"\n          [model]=\"vm.instanceModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.common.variables.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.common.variables.label' | translate }}\"\n        iconClass=\"d-icon-arrows_right\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"variables\"\n      >\n        <rx-form-builder\n          *ngIf=\"vm.variablesInspectorConfig; else noVariables\"\n          [config]=\"vm.variablesInspectorConfig\"\n          [model]=\"vm.variablesModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n\n        <ng-template #noVariables>\n          <adapt-alert\n            class=\"p-3\"\n            [config]=\"{\n              content: 'com.bmc.arsys.rx.client.process-instance-viewer.no-variables.label' | translate,\n              variant: 'info',\n              type: 'inline'\n            }\"\n          ></adapt-alert>\n        </ng-template>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.activity-details.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.process-instance-viewer.activity-details.label' | translate }}\"\n        iconClass=\"d-icon-arrow_chart\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"activity-details\"\n      >\n        <rx-form-builder\n          *ngIf=\"vm.selectedActivityResultsInspectorConfig; else noSelectedActivity\"\n          [config]=\"vm.selectedActivityResultsInspectorConfig\"\n          [model]=\"vm.selectedActivityResultsModel\"\n          [isReadOnly]=\"true\"\n        ></rx-form-builder>\n\n        <ng-template #noSelectedActivity>\n          <adapt-alert\n            class=\"p-3\"\n            [config]=\"{\n              content: 'com.bmc.arsys.rx.client.process-instance-viewer.no-selected-activity.label' | translate,\n              variant: 'info',\n              type: 'inline'\n            }\"\n          ></adapt-alert>\n        </ng-template>\n      </adapt-sidebar-item>\n\n      <adapt-sidebar-item\n        headerTitle=\"{{ 'com.bmc.arsys.rx.client.common.errors.label' | translate }}\"\n        tooltipText=\"{{ 'com.bmc.arsys.rx.client.common.errors.label' | translate }}\"\n        [iconClass]=\"vm.processExecutionErrorSectionIcon\"\n        [destroyOnDeactivate]=\"false\"\n        rx-id=\"errors\"\n      >\n        <rx-validation-issues\n          [issueSections]=\"vm.processExecutionErrorSections\"\n          [emptyText]=\"errorsEmptyText\"\n        ></rx-validation-issues>\n      </adapt-sidebar-item>\n\n      <div class=\"main\">\n        <rx-process-canvas\n          [ngClass]=\"{\n            'inspector-expanded': sidebar.isPanelOpened\n          }\"\n          [config]=\"vm.canvasConfig\"\n          [graph]=\"graph\"\n          (elementSelected)=\"onElementSelected($event)\"\n          (load)=\"onLoad()\"\n        ></rx-process-canvas>\n      </div>\n    </adapt-sidebar>\n  </div>\n\n  <adapt-code-viewer\n    *ngIf=\"!vm.isDesignMode\"\n    class=\"full-size\"\n    theme=\"light\"\n    lang=\"javascript\"\n    [hasToolbar]=\"false\"\n    [code]=\"vm.processInstance | json\"\n  ></adapt-code-viewer>\n</ng-container>\n", styles: [":host{display:flex;flex-direction:column;height:100%;width:100%}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper{border-top:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper .adapt-sidebar-main{overflow:auto}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper .adapt-sidebar-panel-content{padding:1px 0 0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper rx-form-builder rx-form-section:first-child adapt-accordion-tab>.card,:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper rx-validation-issues adapt-accordion adapt-accordion-tab:first-of-type>.card{border-top:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper adapt-accordion-tab>.card{border-radius:0;border-left:0;border-right:0}:host ::ng-deep adapt-sidebar .adapt-sidebar-wrapper adapt-accordion-tab>.card .card-header{border-radius:0}.rx-designer-component{height:calc(100% - 50px)}.rx-designer-container{display:flex;flex-direction:column;flex-grow:1}:root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host::ng-deep rx-process-canvas{left:0}.rx-designer-component .inspector-expanded{right:335px}\n"] }]
        }], ctorParameters: function () { return [{ type: i2$2.RxCommandFactoryService }, { type: i2.RxDateUtilsService }, { type: i2$2.RxDefinitionNameService }, { type: i2$2.RxGlobalCacheService }, { type: i1.RxProcessDefinitionAdapterService }, { type: i2$1.RxProcessDefinitionCacheService }, { type: i1.RxProcessDesignerCacheService }, { type: i1.RxProcessDesignerService }, { type: RxProcessElementHighlighterService }, { type: i2$1.RxProcessElementRegistryService }, { type: i2$1.RxProcessInstanceService }, { type: RxProcessInstanceViewerService }, { type: i2.RxTreeService }, { type: i7.TranslateService }]; }, propDecorators: { configuration: [{
                type: Input
            }], close: [{
                type: Output
            }], canvas: [{
                type: ViewChild,
                args: [RxProcessCanvasComponent, { static: false }]
            }], sidebar: [{
                type: ViewChild,
                args: [AdaptSidebarComponent, { static: false }]
            }] } });

class RxProcessInstanceViewerPageComponent {
    constructor(activatedRoute, router, rxBundleCacheService, rxDefinitionNameService, rxGlobalCacheService, rxPageTitleService, translateService) {
        this.activatedRoute = activatedRoute;
        this.router = router;
        this.rxBundleCacheService = rxBundleCacheService;
        this.rxDefinitionNameService = rxDefinitionNameService;
        this.rxGlobalCacheService = rxGlobalCacheService;
        this.rxPageTitleService = rxPageTitleService;
        this.translateService = translateService;
        this.isInitialized = false;
    }
    ngOnInit() {
        this.subscription = this.activatedRoute.params.subscribe(({ definitionName, instanceId, bundleId }) => {
            this.isInitialized = true;
            this.rxBundleCacheService.bundleId = bundleId || this.rxDefinitionNameService.getBundleId(definitionName);
            this.configuration = {
                ...this.configuration,
                bundleId: this.rxBundleCacheService.bundleId,
                definitionName,
                instanceId
            };
            this.rxGlobalCacheService
                .getBundleFriendlyName(this.rxBundleCacheService.bundleId)
                .pipe(take(1))
                .subscribe((bundleFriendlyName) => {
                this.rxPageTitleService.set([
                    this.rxDefinitionNameService.getDisplayName(definitionName),
                    this.translateService.instant('com.bmc.arsys.rx.client.process-instance-viewer.process-viewer.label'),
                    bundleFriendlyName
                ], this.rxGlobalCacheService.applicationId);
            });
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    onClose() {
        this.router.navigate([
            RX_APPLICATION.innovationStudioBundleId,
            'process',
            'manage',
            this.rxBundleCacheService.bundleId
        ]);
    }
}
RxProcessInstanceViewerPageComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageComponent, deps: [{ token: i1$1.ActivatedRoute }, { token: i1$1.Router }, { token: i2$2.RxBundleCacheService }, { token: i2$2.RxDefinitionNameService }, { token: i2$2.RxGlobalCacheService }, { token: i2$2.RxPageTitleService }, { token: i7.TranslateService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessInstanceViewerPageComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessInstanceViewerPageComponent, selector: "process-instance-viewer-page", ngImport: i0, template: "<rx-process-instance-viewer\n  *ngIf=\"isInitialized\"\n  [configuration]=\"configuration\"\n  (close)=\"onClose()\"\n></rx-process-instance-viewer>\n", dependencies: [{ kind: "directive", type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: RxProcessInstanceViewerComponent, selector: "rx-process-instance-viewer", inputs: ["configuration"], outputs: ["close"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageComponent, decorators: [{
            type: Component,
            args: [{ selector: 'process-instance-viewer-page', template: "<rx-process-instance-viewer\n  *ngIf=\"isInitialized\"\n  [configuration]=\"configuration\"\n  (close)=\"onClose()\"\n></rx-process-instance-viewer>\n" }]
        }], ctorParameters: function () { return [{ type: i1$1.ActivatedRoute }, { type: i1$1.Router }, { type: i2$2.RxBundleCacheService }, { type: i2$2.RxDefinitionNameService }, { type: i2$2.RxGlobalCacheService }, { type: i2$2.RxPageTitleService }, { type: i7.TranslateService }]; } });

class RxProcessInstanceViewerModule {
}
RxProcessInstanceViewerModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RxProcessInstanceViewerModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerModule, declarations: [RxProcessInstanceViewerComponent], imports: [AdaptAlertModule,
        AdaptCodeViewerModule,
        AdaptRxTextareaModule,
        AdaptSidebarModule,
        CommonModule,
        RuntimeViewModule,
        RxDesignerHeaderModule,
        RxFormBuilderModule,
        RxProcessCanvasComponent,
        RxValidationIssuesModule,
        TranslateModule], exports: [RxProcessInstanceViewerComponent] });
RxProcessInstanceViewerModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerModule, imports: [AdaptAlertModule,
        AdaptCodeViewerModule,
        AdaptRxTextareaModule,
        AdaptSidebarModule,
        CommonModule,
        RuntimeViewModule,
        RxDesignerHeaderModule,
        RxFormBuilderModule,
        RxProcessCanvasComponent,
        RxValidationIssuesModule,
        TranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RxProcessInstanceViewerComponent],
                    imports: [
                        AdaptAlertModule,
                        AdaptCodeViewerModule,
                        AdaptRxTextareaModule,
                        AdaptSidebarModule,
                        CommonModule,
                        RuntimeViewModule,
                        RxDesignerHeaderModule,
                        RxFormBuilderModule,
                        RxProcessCanvasComponent,
                        RxValidationIssuesModule,
                        TranslateModule
                    ],
                    exports: [RxProcessInstanceViewerComponent]
                }]
        }] });

class RxProcessInstanceViewerPageModule {
}
RxProcessInstanceViewerPageModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RxProcessInstanceViewerPageModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageModule, declarations: [RxProcessInstanceViewerPageComponent], imports: [CommonModule, RxProcessInstanceViewerModule], exports: [RxProcessInstanceViewerPageComponent] });
RxProcessInstanceViewerPageModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageModule, imports: [CommonModule, RxProcessInstanceViewerModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessInstanceViewerPageModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [RxProcessInstanceViewerPageComponent],
                    imports: [CommonModule, RxProcessInstanceViewerModule],
                    exports: [RxProcessInstanceViewerPageComponent]
                }]
        }] });

class RxProcessPreviewComponent {
    constructor(element, rxProcessDefinitionCacheService, rxProcessElementSearchService, rxRappidPaperService) {
        this.element = element;
        this.rxProcessDefinitionCacheService = rxProcessDefinitionCacheService;
        this.rxProcessElementSearchService = rxProcessElementSearchService;
        this.rxRappidPaperService = rxRappidPaperService;
        this.zoomToFit = false;
        this.destroyed$ = new ReplaySubject(1);
        this.rxProcessDefinitionCacheService.registerConsumer(this.destroyed$);
    }
    ngOnInit() {
        const config$ = this.config.pipe(distinctUntilChanged(isEqual), filter$1((config) => config.processDefinitionName), tap((config) => {
            this.onClick = config.onClick;
            this.processDefinitionName = config.processDefinitionName;
            this.zoomToFit = config.zoomToFit;
        }), switchMap((config) => {
            return this.rxProcessDefinitionCacheService.getProcessDefinition(config.processDefinitionName).pipe(tap((processDefinition) => {
                this.processDefinition = processDefinition;
            }));
        }), shareReplay(1));
        config$
            .pipe(tap(() => this.initialize()), takeUntil(this.destroyed$))
            .subscribe();
    }
    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
    initialize() {
        let graph;
        if (this.processDefinition.layout) {
            graph = this.rxRappidPaperService.getGraph(this.processDefinition);
        }
        else {
            graph = this.getGraph(this.processDefinition);
        }
        this.paperScroller = this.rxRappidPaperService.init(this.element.nativeElement, graph, this.zoomToFit);
        if (this.onClick) {
            this.paperScroller.options.paper.on('blank:pointerdown', () => {
                this.onClick({
                    processDefinition: this.processDefinition,
                    cellView: null
                });
            });
            this.paperScroller.options.paper.on('cell:pointerup', (cellView) => {
                this.onClick({
                    processDefinition: this.processDefinition,
                    cellView: cellView
                });
            });
        }
    }
    zoomIn() {
        this.paperScroller.zoom(0.2, { max: 4 });
    }
    zoomOut() {
        this.paperScroller.zoom(-0.2, { min: 0.2 });
    }
    getGraph(processDefinition) {
        const actionsLength = this.rxProcessElementSearchService.filter(processDefinition, (element) => {
            return (element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.processAction ||
                element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.userTask ||
                element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.receiveTask ||
                element.resourceType === RX_PROCESS_DEFINITION.processElementResourceTypes.callActivity);
        }).length;
        const start = new shapes.bpmn.Event({
            size: {
                width: 50,
                height: 50
            },
            attrs: {
                '.label': {
                    fill: 'gray',
                    text: 'Start'
                }
            },
            eventType: 'start',
            position: {
                x: 50,
                y: 50
            }
        });
        const startCount = new shapes.basic.Rect({
            size: {
                width: 30,
                height: 30
            },
            attrs: {
                text: {
                    // @ts-ignore
                    text: processDefinition.inputParams.length || 0
                },
                g: {
                    'stroke-opacity': 0
                }
            },
            position: {
                x: 60,
                y: 60
            }
        });
        const actionLabel = new shapes.basic.Rect({
            size: {
                width: 30,
                height: 30
            },
            attrs: {
                text: {
                    text: 'Actions',
                    fill: 'gray'
                },
                g: {
                    'stroke-opacity': 0
                }
            },
            position: {
                x: 235,
                y: 110
            }
        });
        const endCount = new shapes.basic.Rect({
            size: {
                width: 30,
                height: 30
            },
            attrs: {
                text: {
                    text: '1'
                },
                g: {
                    'stroke-opacity': 0
                }
            },
            position: {
                x: 410,
                y: 60
            }
        });
        const end = new shapes.bpmn.Event({
            size: {
                width: 50,
                height: 50
            },
            attrs: {
                '.label': {
                    fill: 'gray',
                    text: RX_PROCESS_DEFINITION.processElementDisplayNames.endEvent
                }
            },
            eventType: 'end',
            position: {
                x: 400,
                y: 50
            }
        });
        const action = new shapes.basic.Rect({
            size: {
                width: 200,
                height: 50
            },
            attrs: {
                text: {
                    text: String(actionsLength)
                }
            },
            position: {
                x: 150,
                y: 50
            },
            eventType: 'start'
        });
        const startActionLink = new shapes.bpmn.Flow({
            source: {
                x: 100,
                y: 75
            },
            target: {
                id: action.id
            }
        });
        const actionEndLink = new shapes.bpmn.Flow({
            source: {
                id: action.id
            },
            target: {
                x: 400,
                y: 75
            }
        });
        const graph = new dia.Graph();
        graph.addCells([start, end, action, startActionLink, actionEndLink, startCount, endCount, actionLabel]);
        return graph.toJSON();
    }
}
RxProcessPreviewComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewComponent, deps: [{ token: i0.ElementRef }, { token: i2$1.RxProcessDefinitionCacheService }, { token: i2$1.RxProcessElementSearchService }, { token: RxRappidPaperService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessPreviewComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessPreviewComponent, selector: "rx-process-preview", inputs: { config: "config" }, ngImport: i0, template: '', isInline: true, styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep .joint-element{cursor:default}:host::ng-deep .joint-link{color:#f9f9fa}:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .tool-remove,:host::ng-deep .joint-link:hover .marker-vertices,:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .link-tools{opacity:0;cursor:default}:host::ng-deep .joint-link .connection-wrap:hover{opacity:0}:host::ng-deep .joint-link .connection-wrap{cursor:default}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rx-process-preview', template: '', styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep .joint-element{cursor:default}:host::ng-deep .joint-link{color:#f9f9fa}:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .tool-remove,:host::ng-deep .joint-link:hover .marker-vertices,:host::ng-deep .joint-link:hover .marker-arrowheads,:host::ng-deep .joint-link:hover .link-tools{opacity:0;cursor:default}:host::ng-deep .joint-link .connection-wrap:hover{opacity:0}:host::ng-deep .joint-link .connection-wrap{cursor:default}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i2$1.RxProcessDefinitionCacheService }, { type: i2$1.RxProcessElementSearchService }, { type: RxRappidPaperService }]; }, propDecorators: { config: [{
                type: Input
            }] } });

class RxProcessPreviewModule {
}
RxProcessPreviewModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
RxProcessPreviewModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewModule, declarations: [RxProcessPreviewComponent], imports: [CommonModule, RuntimeViewModule, TranslateModule], exports: [RxProcessPreviewComponent] });
RxProcessPreviewModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewModule, imports: [CommonModule, RuntimeViewModule, TranslateModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreviewModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, RuntimeViewModule, TranslateModule],
                    declarations: [RxProcessPreviewComponent],
                    exports: [RxProcessPreviewComponent]
                }]
        }] });

class RxProcessPreview2Component {
    constructor(rxProcessDefinitionAdapterService, rxProcessDefinitionCacheService, rxProcessDesignerCacheService, rxProcessDesignerDependencyResolver, rxProcessDesignerService, rxProcessElementRegistryService) {
        this.rxProcessDefinitionAdapterService = rxProcessDefinitionAdapterService;
        this.rxProcessDefinitionCacheService = rxProcessDefinitionCacheService;
        this.rxProcessDesignerCacheService = rxProcessDesignerCacheService;
        this.rxProcessDesignerDependencyResolver = rxProcessDesignerDependencyResolver;
        this.rxProcessDesignerService = rxProcessDesignerService;
        this.rxProcessElementRegistryService = rxProcessElementRegistryService;
        this.customActionsSubject = new BehaviorSubject(null);
        this.dependencyResolver$ = this.rxProcessDesignerDependencyResolver.resolve().pipe(shareReplay(1));
        this.destroyed$ = new ReplaySubject(1);
        this.busySubscription = NEVER.subscribe();
        this.graph = new RxDiagramGraph(null, {
            cellNamespace: this.rxProcessElementRegistryService.cellNamespace
        });
        this.canvasConfig$ = combineLatest([this.customActionsSubject, this.dependencyResolver$]).pipe(map$1(([customActions]) => ({
            enableMultiSelection: false,
            enableSnaplines: false,
            leftCustomActions: customActions,
            canSelectElement: () => false,
            interactive: () => false
        })));
        this.rxProcessDefinitionCacheService.registerConsumer(this.destroyed$);
        this.rxProcessDesignerCacheService.registerConsumer(this.destroyed$);
    }
    ngOnInit() {
        this.processDefinition$ = this.config.pipe(distinctUntilChanged(isEqual), map$1((config) => config.processDefinitionName), switchMap((processDefinitionName) => this.rxProcessDefinitionCacheService.getProcessDefinition(processDefinitionName)), map$1((processDefinition) => this.rxProcessDefinitionAdapterService.adaptProcessDefinition(processDefinition)), tap((processDefinition) => {
            this.rxProcessDesignerCacheService.setCurrentProcessDefinition(processDefinition);
        }));
        this.config.pipe(takeUntil(this.destroyed$)).subscribe((config) => {
            this.customActionsSubject.next(config.customActions);
        });
    }
    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
    onLoad() {
        combineLatest([this.processDefinition$, this.config])
            .pipe(take(1))
            .subscribe(([processDefinition, config]) => {
            this.graph.clear({ silent: true, ignoreCommandManager: true });
            this.graph.fromJSON(this.rxProcessDesignerService.getDiagramLayout(processDefinition, null, processDefinition.localizableStrings));
            this.canvas.zoomToFit();
            if (config.onClick) {
                const paper = this.canvas.getPaper();
                paper.on('blank:pointerdown', () => {
                    config.onClick({
                        processDefinition,
                        cellView: null
                    });
                });
                paper.on('cell:pointerup', (elementView) => {
                    config.onClick({
                        processDefinition,
                        cellView: elementView
                    });
                });
            }
            this.busySubscription.unsubscribe();
        });
    }
}
RxProcessPreview2Component.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreview2Component, deps: [{ token: i1.RxProcessDefinitionAdapterService }, { token: i2$1.RxProcessDefinitionCacheService }, { token: i1.RxProcessDesignerCacheService }, { token: i2$1.RxProcessDesignerDependencyResolver }, { token: i1.RxProcessDesignerService }, { token: i2$1.RxProcessElementRegistryService }], target: i0.ɵɵFactoryTarget.Component });
RxProcessPreview2Component.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: RxProcessPreview2Component, isStandalone: true, selector: "rx-process-preview2", inputs: { config: "config" }, viewQueries: [{ propertyName: "canvas", first: true, predicate: RxProcessCanvasComponent, descendants: true }], ngImport: i0, template: "<rx-process-canvas\n  *ngIf=\"canvasConfig$ | async as canvasConfig\"\n  [config]=\"canvasConfig\"\n  [graph]=\"graph\"\n  (load)=\"onLoad()\"\n></rx-process-canvas>\n\n<rx-busy-indicator [options]=\"{ busy: busySubscription }\"></rx-busy-indicator>\n", styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep rx-process-canvas{position:static}:host::ng-deep .joint-element,:host::ng-deep .joint-link{cursor:default}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i9.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i9.AsyncPipe, name: "async" }, { kind: "component", type: RxProcessCanvasComponent, selector: "rx-process-canvas", inputs: ["config", "commandManager", "graph"], outputs: ["elementSelected", "load"] }, { kind: "ngmodule", type: TranslateModule }, { kind: "ngmodule", type: AdaptBusyModule }, { kind: "ngmodule", type: RxBusyIndicatorModule }, { kind: "component", type: i5.RxBusyIndicatorComponent, selector: "rx-busy-indicator", inputs: ["options"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: RxProcessPreview2Component, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'rx-process-preview2', changeDetection: ChangeDetectionStrategy.OnPush, imports: [CommonModule, RxProcessCanvasComponent, TranslateModule, AdaptBusyModule, RxBusyIndicatorModule], template: "<rx-process-canvas\n  *ngIf=\"canvasConfig$ | async as canvasConfig\"\n  [config]=\"canvasConfig\"\n  [graph]=\"graph\"\n  (load)=\"onLoad()\"\n></rx-process-canvas>\n\n<rx-busy-indicator [options]=\"{ busy: busySubscription }\"></rx-busy-indicator>\n", styles: [":root{--border-radius: 4px;--nav-background: var(--gray-900);--nav-links-color: var(--white);--font-family: \"Open Sans\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, \"Helvetica Neue\", Arial, sans-serif;--color-primary-disabled-hover: #ff8d2a;--color-primary-disabled: #ff7d18;--color-primary: #f86e00;--color-primary-hover: #e45f00;--color-primary-active: #d05100;--color-primary-active-hover: #bc4300;--color-secondary-disabled-hover: #ffffff;--color-secondary-disabled: #ffffff;--color-secondary: #f9f9fa;--color-secondary-hover: #e5e5e6;--color-secondary-active: #d2d2d3;--color-secondary-active-hover: #c0c0c2;--color-active-disabled-hover: #45c8bd;--color-active-disabled: #2db7ad;--color-active: #00a79d;--color-active-hover: #00978e;--color-active-active: #00887f;--color-active-active-hover: #007970;--color-info-disabled-hover: #66d9f1;--color-info-disabled: #52c7df;--color-info: #3cb6ce;--color-info-hover: #21a6bd;--color-info-active: #0096ad;--color-info-active-hover: #00879e;--color-info-muted: #c0e9f2;--color-info-muted-hover: #9cc4cd;--color-info-muted-active: #7ba3ac;--color-info-muted-text: #262626;--color-success-disabled-hover: #aae860;--color-success-disabled: #99d550;--color-success: #89c341;--color-success-hover: #7ab232;--color-success-active: #6ba122;--color-success-active-hover: #5d9110;--color-warning-disabled-hover: #ffda46;--color-warning-disabled: #ffc734;--color-warning: #f1b521;--color-warning-hover: #dea406;--color-warning-active: #cc9400;--color-warning-active-hover: #ba8400;--color-danger-disabled-hover: #ff5323;--color-danger-disabled: #ff4313;--color-danger: #df2d00;--color-danger-hover: #b60100;--color-danger-active: #8c0000;--color-danger-active-hover: #b80000;--white: #ffffff;--gray-100: #fafafa;--gray-200: #f2f2f2;--gray-300: #d9d9d9;--gray-400: #bababa;--gray-500: #999999;--gray-600: #808080;--gray-700: #5e5e5e;--gray-800: #404040;--gray-900: #262626;--gray-1000: #1f1f1f;--black: #000000;--lightCVTagcolor: #fa035a;--lightCVFunctionNameColor: #0242b3;--lightCVFunctionColor: #ad4d00;--lightCVSymbol: #bc890c;--lightCVKeyword: #875ccb;--lightCVVariable: #628d2c;--lightCVEntity: #258699;--lightCVInserted: #008000;--darkCVTagcolor: #ff656c;--darkCVFunctionNameColor: #328dee;--darkCVFunctionColor: #faa159;--darkCVSymbol: #f6cf6f;--darkCVKeyword: #aab0fa;--darkCVVariable: #b2d884;--darkCVEntity: #80d0df;--dense-spacing: 1;--dense-spacing-custom: false;--logo-width: 2.9em;--table-scroll-width: 0;--text-align: left;--close-position: right;--helix-gpt-main-color: #f86e00;--helix-gpt-main-background-primary-top: rgba(255, 176, 75, .25);--helix-gpt-main-background-primary-bottom: rgba(248, 110, 0, .25);--helix-gpt-card-primary-border-bottom-color: rgba(248, 110, 0, .4);--helix-gpt-main-background-alt-color: rgba(248, 110, 0, .1);--helix-gpt-main-background-success-top: rgba(207, 255, 131, .25);--helix-gpt-main-background-success-bottom: rgba(137, 195, 65, .25);--helix-gpt-card-success-border-bottom-color: rgba(137, 195, 65, .4);--helix-gpt-main-background-alt-color-success: rgba(137, 195, 65, .1);--helix-gpt-button-background-color: rgba(248, 110, 0, .07);--helix-gpt-button-boxshadow-color: rgba(248, 110, 0, .2)}[data-theme=light],:root{--text-default: var(--gray-900);--text-default-inverse: var(--gray-100);--text-secondary: var(--gray-700);--text-secondary-inverse: var(--gray-300);--text-tertiary: var(--gray-600);--text-tertiary-inverse: var(--gray-400);--color-foreground: var(--white);--color-foreground-inverse: var(--gray-900);--color-background: var(--gray-200);--color-background-inverse: var(--gray-1000);--table-scroll-width: 0;--text-align: start;--close-position: right;--mobile-adder: 0;--mobile-line-height-base: 0;--mobile-header-font: 0;--mobile-additional-padding: 0;--mobile-form-additional-padding: 0;--mobile-additional-px: 0;--mobile-vert-padding: 0r;--white-rgb: 255, 255, 255;--multi-card-separator-color: 0, 44, 117;--helix-gpt-card-background: rgb(255 255 255 / 70%);--helix-gpt-guidance-background-color: linear-gradient(180deg, var(--helix-gpt-main-background-primary-top) 0%, var(--helix-gpt-main-background-primary-bottom) 100%), var(--white);--white-text-color: #ffffff}:root{--base-spacer: 1rem;--spacers-1: .25rem;--spacers-2: .5rem;--spacers-3: .75rem;--spacers-4: 1.25rem;--spacers-5: 2rem;--spacers-6: 3.25rem;--border-color: var(--gray-300)}:host{display:block;height:600px;width:100%}:host::ng-deep rx-process-canvas{position:static}:host::ng-deep .joint-element,:host::ng-deep .joint-link{cursor:default}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.RxProcessDefinitionAdapterService }, { type: i2$1.RxProcessDefinitionCacheService }, { type: i1.RxProcessDesignerCacheService }, { type: i2$1.RxProcessDesignerDependencyResolver }, { type: i1.RxProcessDesignerService }, { type: i2$1.RxProcessElementRegistryService }]; }, propDecorators: { config: [{
                type: Input
            }], canvas: [{
                type: ViewChild,
                args: [RxProcessCanvasComponent, { static: false }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { RxProcessInstancePreview2Component, RxProcessInstancePreviewComponent, RxProcessInstancePreviewModule, RxProcessInstanceViewerPageComponent, RxProcessInstanceViewerPageModule, RxProcessPreview2Component, RxProcessPreviewComponent, RxProcessPreviewModule };
